import { Router } from "express";

import { authController } from "../controllers/auth-controller";
import {
  authenticate,
  createRateLimit,
  requireEmailVerification,
} from "../middleware/auth-middleware";

const router = Router();

// Environment-aware rate limiting configurations
// Development (NODE_ENV=development): All rate limits DISABLED for easier testing
// Production (NODE_ENV=production): Rate limits ENABLED with higher limits for better UX
//
// Rate limiting tiers:
// - authRateLimit: Critical auth endpoints (login/register) - 20 req/15min
// - generalRateLimit: Standard endpoints (password reset, etc.) - 50 req/5min
// - otpRateLimit: OTP endpoints (send/resend OTP) - 10 req/10min
const authRateLimit = createRateLimit.auth();
const generalRateLimit = createRateLimit.general();
const otpRateLimit = createRateLimit.otp();

/**
 * @route   POST /api/v1/auth/register
 * @desc    Register a new user with email and password
 * @access  Public
 * @body    { email, password, role }
 */
router.post("/register", authRateLimit, (req, res) =>
  authController.register(req, res),
);

/**
 * @route   POST /api/v1/auth/verify-email
 * @desc    Verify email address with OTP (supports both signup and forgot password verification)
 * @access  Public
 * @body    { email, otp, type?: "signup" | "forget" }
 */
router.post("/verify-email", generalRateLimit, (req, res) =>
  authController.verifyEmail(req, res),
);

/**
 * @route   POST /api/v1/auth/resend-otp
 * @desc    Resend OTP for email verification or password reset
 * @access  Public
 * @body    { email, type: "email_verification" | "password_reset" }
 */
router.post("/resend-otp", otpRateLimit, (req, res) =>
  authController.resendOTP(req, res),
);

/**
 * @route   POST /api/v1/auth/login
 * @body    email, password, userType (admin|school|teacher|student — must match account role), optional deviceInfo
 * @desc    Login with email and password
 * @access  Public
 * @body    { email, password, deviceInfo }
 */
router.post("/login", authRateLimit, (req, res) =>
  authController.login(req, res),
);

/**
 * @route   POST /api/v1/auth/social-login
 * @desc    Login or register with social provider (Google, Facebook, Apple)
 * @access  Public
 * @body    { provider, providerId, email, name?, profileImage?, role, deviceInfo }
 */
router.post("/social-login", authRateLimit, (req, res) =>
  authController.socialLogin(req, res),
);

/**
 * @route   POST /api/v1/auth/forgot-password
 * @desc    Send password reset OTP to email
 * @access  Public
 * @body    { email }
 */
router.post("/forgot-password", otpRateLimit, (req, res) =>
  authController.forgotPassword(req, res),
);

/**
 * @route   POST /api/v1/auth/reset-password
 * @desc    Reset password (requires auth token from forgot password verification)
 * @access  Private (requires authentication from forgot password flow)
 * @body    { password }
 */
router.post("/reset-password", generalRateLimit, (req, res) =>
  authController.resetPassword(req, res),
);

/**
 * @route   POST /api/v1/auth/change-password
 * @desc    Change password for authenticated user
 * @access  Private (requires authentication)
 * @body    { currentPassword, newPassword }
 */
router.post(
  "/change-password",
  authenticate,
  requireEmailVerification,
  generalRateLimit,
  (req, res) => authController.changePassword(req, res),
);

/**
 * @route   POST /api/v1/auth/refresh
 * @desc    Refresh access token using refresh token
 * @access  Public
 * @body    { refreshToken }
 */
router.post("/refresh", generalRateLimit, (req, res) =>
  authController.refreshToken(req, res),
);

/**
 * @route   POST /api/v1/auth/logout
 * @desc    Logout user and invalidate tokens
 * @access  Private (requires authentication)
 * @body    {}
 */
router.post("/logout", authenticate, (req, res) =>
  authController.logout(req, res),
);

export default router;
